/**
 * English: Billing
 *
 * What this file covers: Credits, plans, top-ups, the ledger, usage, alerts and invoices.
 *
 * Tone: Exact about money. Credits are what is spent; R is used only for rand actually paid. Never use words
 * like margin, markup or profit. Write amounts and dates the way the language writes them.
 *
 * English: English is the reference: every key is added here first, in South African English (organise, colour,
 * cancelled). Every other language falls back to this text for any key it does not have yet.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const billing: Catalog = {
  'billing.currency': 'Currency',
  'billing.description': 'Your wallet balance, transaction history and top-ups.',
  'billing.loadError': 'The wallet could not be loaded.',
  'billing.ledgerError': 'Recent transactions could not be loaded.',
  'billing.empty.title': 'Your wallet is empty',
  'billing.empty.body': 'Sending is blocked until you top up. Agents replying in the inbox will see “Not enough credit to send”, and broadcasts will not start.',
  'billing.lowBalance.bodyWithCount': 'About {{count}} messages left at your recent rate.',
  'billing.noTransactions.hint': 'Top-ups and message charges appear here as they happen.',
  'billing.roughlyMessages': 'About {{count}} messages',
  'billing.perMessage': '{{amount}} a message lately',
  'billing.credit': 'credit',
  'billing.debit': 'debit',
  'billing.pastDue.title': 'Your subscription is past due',
  'billing.pastDue.body': 'Your plan\'s monthly charge did not go through on your card. Update your card and we try again within the hour. Features stay on for now, but the plan ends if it is still unpaid when the grace period runs out.',
  'billing.pastDue.action': 'Update your card',
  'billing.invoices': 'Invoices',
  'billing.invoices.empty': 'No invoices yet',
  'billing.invoices.empty.hint': 'Invoices are issued at the end of each billing period.',
  'billing.invoices.notIssued': 'Not issued yet',
  'billing.invoiceStatus.draft': 'Draft',
  'billing.invoiceStatus.issued': 'Issued',
  'billing.invoiceStatus.paid': 'Paid',
  'billing.invoiceStatus.void': 'Void',
  'billing.fullLedger': 'Full ledger →',
  'billing.ledger.columns.when': 'When',
  'billing.ledger.description': 'Every credit and debit on your wallet.',
  'billing.ledger.loadError': 'Failed to load the ledger.',
  'billing.ledger.noMatch': 'No ledger entries match.',
  'billing.ledger.searchPlaceholder': 'Search description / type…',
  'billing.ledger.title': 'Ledger',
  'billing.ledger.entries': 'Entries',
  'billing.ledger.creditedTotal': 'Added',
  'billing.ledger.debitedTotal': 'Spent',
  'billing.ledger.amount': 'Amount',
  'billing.ledger.type': 'Type',
  'billing.ledger.type.any': 'Any type',
  'billing.ledger.sku': 'What for',
  'billing.ledger.range': 'Period',
  'billing.ledger.range.all': 'All time',
  'billing.ledger.range.7d': 'Last 7 days',
  'billing.ledger.range.30d': 'Last 30 days',
  'billing.ledger.range.90d': 'Last 90 days',
  'billing.ledger.search': 'Search transactions',
  'billing.ledger.search.placeholder': 'Search description or type',
  'billing.ledger.emptyFiltered': 'Nothing in this period',
  'billing.ledger.exported': 'Ledger downloaded.',
  'billing.entryType.topup': 'Top-up',
  'billing.entryType.grant': 'Granted',
  'billing.entryType.debit': 'Message charge',
  'billing.entryType.refund': 'Refund',
  'billing.entryType.adjustment': 'Adjustment',
  'billing.entryType.plan': 'Plan charge',
  'billing.lowBalance': 'Low balance',
  'billing.emptyBalance': 'Out of credit',
  'billing.lowBalance.body': 'Your wallet is running low. Top up to keep sending messages.',
  'billing.noTransactionsYet': 'No transactions yet.',
  'billing.recentTransactions': 'Recent transactions',
  'billing.topUp': 'Top up →',
  'billing.topUpNow': 'Top up now →',
  'billing.topup.description': 'Buy credits. One credit is one rand of messaging; naira and shillings convert at the current rate.',
  'billing.topup.amount': 'How much to pay ({{currency}})',
  'billing.topup.custom': 'Or another amount',
  'billing.topup.custom.hint': 'Between {{min}} and {{max}}.',
  'billing.topup.tooSmall': 'The smallest top-up is {{min}}.',
  'billing.topup.tooLarge': 'The largest top-up in one go is {{max}}. Contact us for more.',
  'billing.topup.afterTopUp': 'After this top-up',
  'billing.topup.buys': '{{amount}} buys {{credits}}, roughly {{count}} messages at your recent rate.',
  'billing.topup.provider': 'Pay with',
  'billing.topup.provider.unavailable': '{{name}} (not available yet)',
  'billing.topup.noProvider.title': 'No payment method available',
  'billing.topup.noProvider.body': 'We cannot take {{currency}} payments online yet. Contact support to top up.',
  'billing.topup.unknownCurrency': 'Your wallet is in {{currency}}, which has no preset amounts. Enter one below.',
  'billing.topup.confirm.title': 'Go to the payment page?',
  'billing.topup.confirm.body': 'You will leave RepletoryReach to pay {{amount}} with {{provider}}.',
  'billing.topup.confirm.hint': 'Your balance updates once the payment clears, which is usually immediate.',
  'billing.topup.confirm.go': 'Continue to pay',
  'billing.topup.starting': 'Setting up the payment…',
  'billing.topup.redirecting': 'Taking you to the payment page…',
  'billing.topup.returned': 'Welcome back. If the payment went through, your balance is already updated.',
  'billing.topup.reference': 'Payment reference: {{reference}}. Quote it if you need to ask about this payment.',
  'billing.topup.notConfigured': 'Online payments are not set up on this account yet. Contact support to top up.',
  'billing.topup.providerError': 'The payment provider could not be reached. Try again shortly.',
  'billing.topup.unknownProvider': 'That payment method is not available.',
  'billing.topup.howItWorks': 'How top-ups work',
  'billing.topup.howItWorks.body': 'Credits are added to your wallet as soon as the payment clears: one credit for every rand paid, and naira or shillings at the current rate. Credits are spent per message as you send and do not expire.',
  'billing.topup.failed': 'Could not start the top-up.',
  'billing.topup.noCheckoutUrl': 'The gateway did not return a checkout URL.',
  'billing.topup.pay': 'Pay',
  'billing.topup.paymentMethod': 'Payment method',
  'billing.topup.redirectNotice': 'You\'ll be redirected to {{provider}} to complete payment. Your wallet credits as soon as the gateway confirms.',
  'billing.topup.topUp': 'Top-up',
  'billing.walletBalance': 'Wallet balance',
  'billing.invoices.view': 'View the invoice',
  'billing.invoices.issuedAt': 'Issued',
  'billing.usage.title': 'Usage',
  'billing.usage.description': 'Where your credit goes: every message, bot reply, model token, file and notification, priced as it happened.',
  'billing.usage.period': 'Period',
  'billing.usage.period.month': 'This month',
  'billing.usage.period.7d': 'Last 7 days',
  'billing.usage.period.30d': 'Last 30 days',
  'billing.usage.period.90d': 'Last 90 days',
  'billing.usage.period.previous_month': 'Previous month',
  'billing.usage.loadError': 'Could not load usage.',
  'billing.usage.exported': 'Usage exported.',
  'billing.usage.stateHint': 'Balance {{balance}}, low-balance line {{threshold}}.',
  'billing.usage.manageAlerts': 'Alerts and auto top-up',
  'billing.usage.kpi.spent': 'Spent',
  'billing.usage.kpi.spentHint': '{{count}} charges',
  'billing.usage.kpi.perDay': 'A day, on average',
  'billing.usage.kpi.projected': 'About {{amount}} by month end at this pace',
  'billing.usage.kpi.projectedLabel': 'Month end, at this pace',
  'billing.usage.kpi.runway': 'Balance lasts',
  'billing.usage.kpi.runwayDays': '{{count}} days',
  'billing.usage.kpi.runwayNone': 'No spend yet',
  'billing.usage.kpi.runwayHint': 'At the period\'s average daily spend',
  'billing.usage.kpi.lowLine': 'Alerts under {{amount}}',
  'billing.usage.daily.title': 'Spend by day',
  'billing.usage.daily.empty': 'Not enough days yet',
  'billing.usage.daily.emptyHint': 'The chart appears once there are two days of charges in the period.',
  'billing.usage.byCategory.title': 'By kind',
  'billing.usage.byCategory.hint': 'WhatsApp is priced per message by Meta\'s category and the recipient\'s country; AI per thousand tokens; the rest per unit as listed under Rates.',
  'billing.usage.noSpend': 'Nothing charged in this period',
  'billing.usage.noSpendHint': 'Charges appear here the moment a message, bot reply or file goes through.',
  'billing.usage.entriesCount': '{{count}} charges',
  'billing.usage.tabs.activity': 'By activity',
  'billing.usage.tabs.items': 'By item',
  'billing.usage.tabs.broadcasts': 'Broadcasts',
  'billing.usage.tabs.contacts': 'Contacts',
  'billing.usage.tabs.flows': 'Flows',
  'billing.usage.tabs.rates': 'Rates',
  'billing.usage.columns.activity': 'Activity',
  'billing.usage.columns.entries': 'Charges',
  'billing.usage.columns.share': 'Share',
  'billing.usage.columns.item': 'Item',
  'billing.usage.columns.units': 'Units',
  'billing.usage.columns.rate': 'Rate',
  'billing.usage.columns.per': 'Per',
  'billing.usage.columns.messages': 'Messages',
  'billing.usage.columns.lastActivity': 'Last activity',
  'billing.usage.ratePer': '{{amount}} a {{unit}}',
  'billing.usage.viewEntries': 'Entries',
  'billing.usage.noBroadcasts': 'No broadcast spend in this period',
  'billing.usage.noContacts': 'No contact spend in this period',
  'billing.usage.noFlows': 'No flow spend in this period',
  'billing.usage.broadcastLink': 'Broadcast {{id}}',
  'billing.usage.flowLink': 'Flow {{id}}',
  'billing.usage.rates.hint': 'What each unit costs, in credits. One credit is one rand of messaging. Nothing is charged that is not on this list.',
  'billing.usage.rates.empty': 'No rates published',
  'billing.usage.rates.converted': 'converted',
  'billing.usage.drawer.summary': '{{count}} entries',
  'billing.usage.drawer.more': 'Show more',
  'billing.usage.thisMonth': 'This month',
  'billing.usage.thisMonthHint': 'Spend so far, the pace, and how long the balance lasts at it.',
  'billing.usage.fullUsage': 'Full usage →',
  'billing.autoTopup.on': 'Auto top-up is on',
  'billing.autoTopup.setUp': 'Set up auto top-up',
  'billing.autoTopup.onHint': 'Automatic top-up is on: {{amount}} from {{method}} when the balance drops under your threshold.',
  'billing.autoTopup.offHint': 'Automatic top-up is off. You will be alerted under {{threshold}}; sending stops at zero.',
  'billing.source.broadcast': 'Broadcast',
  'billing.source.flow': 'Bot reply',
  'billing.source.agent': 'Agent reply',
  'billing.source.inbound': 'Message received',
  'billing.source.message': 'Message sent',
  'billing.source.kb': 'Knowledge base',
  'billing.source.notify': 'Notification',
  'billing.source.media': 'Media',
  'billing.source.web': 'Website chat',
  'billing.source.topup': 'Top-up',
  'billing.source.auto_topup': 'Automatic top-up',
  'billing.source.manual': 'Adjustment',
  'billing.source.plan': 'Plan',
  'billing.source.platform': 'Platform',
  'billing.source.grant': 'Granted',
  'billing.source.refund': 'Refund',
  'billing.source.adjustment': 'Adjustment',
  'billing.source.debit': 'Charge',
  'billing.category.whatsapp': 'WhatsApp',
  'billing.category.ai': 'AI',
  'billing.category.knowledge': 'Knowledge base',
  'billing.category.notifications': 'Notifications',
  'billing.category.media': 'Media',
  'billing.category.web': 'Website chat',
  'billing.category.other': 'Other',
  'billing.unit.message.one': 'message',
  'billing.unit.message.other': 'messages',
  'billing.unit.1k_tokens.one': 'thousand tokens',
  'billing.unit.1k_tokens.other': 'thousand tokens',
  'billing.unit.document.one': 'document',
  'billing.unit.document.other': 'documents',
  'billing.unit.question.one': 'question',
  'billing.unit.question.other': 'questions',
  'billing.unit.file.one': 'file',
  'billing.unit.file.other': 'files',
  'billing.unit.gb_day.one': 'GB-day',
  'billing.unit.gb_day.other': 'GB-days',
  'billing.unit.call.one': 'call',
  'billing.unit.call.other': 'calls',
  'billing.unit.topup.one': 'top-up',
  'billing.unit.topup.other': 'top-ups',
  'billing.actor.flows': 'The bot',
  'billing.actor.broadcasts': 'A broadcast',
  'billing.actor.channel-wa': 'The WhatsApp channel',
  'billing.actor.billing': 'The platform',
  'billing.actor.media': 'The media service',
  'billing.actor.user': 'A person on your team',
  'billing.explain.title': 'About this entry',
  'billing.explain.charged': 'Charged',
  'billing.explain.credited': 'Credited',
  'billing.explain.workedOut': 'Worked out as',
  'billing.explain.formula': '{{units}} {{unit}} × {{rate}} a {{per}}',
  'billing.explain.what': 'What',
  'billing.explain.contact': 'Contact',
  'billing.explain.run': 'run {{id}}',
  'billing.explain.openConversation': 'Open the conversation',
  'billing.explain.by': 'Caused by',
  'billing.explain.details': 'Details',
  'billing.explain.footnote': 'Every figure comes from the billing ledger as it was written at the time; a later rate change does not alter it.',
  'billing.meta.model': 'Model',
  'billing.meta.direction': 'Direction',
  'billing.meta.tokens': 'Tokens',
  'billing.meta.purpose': 'Purpose',
  'billing.meta.channel': 'Channel',
  'billing.meta.template': 'Template',
  'billing.meta.category': 'Category',
  'billing.meta.country': 'Country',
  'billing.meta.msg_type': 'Message type',
  'billing.meta.rate_name': 'Rate',
  'billing.meta.fx_per_zar': 'Exchange rate (per ZAR)',
  'billing.meta.quoted_currency': 'Quoted in',
  'billing.meta.bytes': 'Bytes',
  'billing.meta.files': 'Files',
  'billing.meta.day': 'Day',
  'billing.meta.chunks': 'Passages',
  'billing.meta.characters': 'Characters',
  'billing.meta.size_bytes': 'Size (bytes)',
  'billing.meta.mime': 'Type',
  'billing.ledger.for': 'For',
  'billing.ledger.for.any': 'Anything',
  'billing.ledger.explain': 'Explain',
  'billing.ledger.unitsAtRate': '{{units}} × {{rate}}',
  'billing.alerts.title': 'Alerts and auto top-up',
  'billing.alerts.description': 'Be told before the credit runs out, and let the platform top up for you.',
  'billing.alerts.loadError': 'Could not load your alert settings.',
  'billing.alerts.saved': 'Settings saved.',
  'billing.alerts.saveFailed': 'Could not save the settings.',
  'billing.alerts.state.ok': 'Above your low-balance line',
  'billing.alerts.state.low': 'Under your low-balance line',
  'billing.alerts.state.empty': 'Empty: sending has stopped',
  'billing.alerts.lowLine': 'Low-balance line',
  'billing.alerts.lowLine.default': 'The default for your currency',
  'billing.alerts.lowLine.custom': 'Set by you',
  'billing.alerts.low.title': 'Low balance alert',
  'billing.alerts.low.body': 'When the balance drops under the line you are told by email (and by WhatsApp if you give a number). At zero you are told again, because sending has stopped.',
  'billing.alerts.low.threshold': 'Alert me when the balance drops under (credits)',
  'billing.alerts.low.thresholdHint': 'In credits. Leave blank for the default, {{amount}}.',
  'billing.alerts.low.email': 'Email',
  'billing.alerts.low.emailHint': 'One or more addresses, separated by commas. Blank sends to everyone on your organisation.',
  'billing.alerts.low.msisdn': 'WhatsApp number (optional)',
  'billing.alerts.low.msisdnHint': 'A text from your own WhatsApp number. It only arrives if that number has messaged your business in the last 24 hours.',
  'billing.alerts.low.inApp': 'Show alerts in the console',
  'billing.alerts.low.inAppHint': 'The sidebar and the billing page turn amber when low and red when empty.',
  'billing.alerts.test.action': 'Send a test alert',
  'billing.alerts.test.sentTo': 'Test alert sent to {{target}}.',
  'billing.alerts.test.recorded': 'Test alert recorded. Email: {{detail}}',
  'billing.alerts.test.failed': 'The test alert could not be sent.',
  'billing.alerts.auto.title': 'Automatic top-up',
  'billing.alerts.auto.body': 'When the balance drops under the threshold, the platform charges your saved card for the amount below, at most the number of times a day you allow.',
  'billing.alerts.auto.enable': 'Top up automatically',
  'billing.alerts.auto.needsCard': 'Save a card below first.',
  'billing.alerts.auto.threshold': 'Top up when the balance drops under (credits)',
  'billing.alerts.auto.thresholdHint': 'Blank uses the low-balance line.',
  'billing.alerts.auto.amount': 'Amount to pay ({{currency}})',
  'billing.alerts.auto.amountHint': 'Charged to the saved card each time. If it is less than the threshold, the platform charges again until the balance clears the line, up to the daily cap.',
  'billing.alerts.auto.cap': 'At most, a day',
  'billing.alerts.auto.capHint': 'Protects you from a runaway broadcast. {{count}} so far today.',
  'billing.alerts.auto.method': 'Card to charge',
  'billing.alerts.auto.methodPrimary': 'The primary card',
  'billing.alerts.auto.methodHint': 'The card is charged without you present, through the gateway that saved it.',
  'billing.alerts.auto.chargeNow': 'Top up now',
  'billing.alerts.auto.chargeNowBody': 'Charge the saved card {{amount}} now?',
  'billing.alerts.auto.charged': 'Charged {{amount}}. The balance is {{balance}}.',
  'billing.alerts.auto.notCharged': 'Not charged',
  'billing.alerts.auto.lastHint': 'Last attempt {{when}}: {{outcome}}',
  'billing.alerts.auto.succeeded': 'succeeded',
  'billing.alerts.auto.failed': 'failed',
  'billing.alerts.auto.never': 'Never run',
  'billing.alerts.methods.title': 'Saved cards',
  'billing.alerts.methods.body': 'The card number never reaches us: the gateway keeps it and gives us a token to charge. Saving a card is a small charge that is credited to your wallet.',
  'billing.alerts.methods.add': 'Save a card',
  'billing.alerts.methods.empty': 'No saved cards',
  'billing.alerts.methods.emptyHint': 'Save one to turn on automatic top-up.',
  'billing.alerts.methods.primary': 'Primary',
  'billing.alerts.methods.primaryLabel': '{{label}} (primary)',
  'billing.alerts.methods.notReusable': 'Cannot be charged again',
  'billing.alerts.methods.expires': 'Expires {{month}}/{{year}}',
  'billing.alerts.methods.added': 'saved {{when}}',
  'billing.alerts.methods.makePrimary': 'Make primary',
  'billing.alerts.methods.remove.title': 'Forget this card?',
  'billing.alerts.methods.remove.body': '{{label}} will not be charged again. Automatic top-up turns off if it was the last card.',
  'billing.alerts.methods.autoOff': 'Automatic top-up is off: there is no card left to charge.',
  'billing.alerts.methods.returned': 'Welcome back. The card appears here once the gateway confirms it, usually within a few seconds.',
  'billing.alerts.methods.noUrl': 'The gateway did not return a checkout page.',
  'billing.alerts.methods.startFailed': 'Could not start saving a card.',
  'billing.alerts.methods.confirm.title': 'Go to the payment page?',
  'billing.alerts.methods.confirm.body': 'You will leave RepletoryReach to enter the card at Paystack. A charge of {{amount}} saves it and is credited to your wallet.',
  'billing.alerts.methods.confirm.go': 'Continue to Paystack',
  'billing.alerts.history.title': 'Alert history',
  'billing.alerts.history.body': 'Every alert sent, per channel, with what happened to it.',
  'billing.alerts.history.empty': 'No alerts sent yet',
  'billing.alerts.history.kind': 'Alert',
  'billing.alerts.history.channel': 'Channel',
  'billing.alerts.history.target': 'To',
  'billing.alerts.kind.low_balance': 'Low balance',
  'billing.alerts.kind.depleted': 'Empty',
  'billing.alerts.kind.recovered': 'Recovered',
  'billing.alerts.kind.auto_topup_succeeded': 'Auto top-up succeeded',
  'billing.alerts.kind.auto_topup_failed': 'Auto top-up failed',
  'billing.alerts.channel.email': 'Email',
  'billing.alerts.channel.whatsapp': 'WhatsApp',
  'billing.alerts.channel.webhook': 'Webhook',
  'billing.alerts.channel.in_app': 'Console',
  'billing.alerts.status.sent': 'Sent',
  'billing.alerts.status.failed': 'Failed',
  'billing.alerts.status.skipped': 'Skipped',
  'billing.alerts.footnote': 'Alerts follow the same ledger the usage page reads.',
  'billing.alerts.daily.threshold': 'Tell me when I spend more than this in a day ({{currency}})',
  'billing.alerts.daily.thresholdHint': 'Catches a runaway bot or a broadcast bigger than intended. Blank turns it off. Days run on UTC.',
  'billing.alerts.kind.daily_spend': 'Daily spend',
  'billing.statements.generate': 'Statement for {{period}}',
  'billing.statements.hint': 'A usage statement is a paid tax invoice for one month\'s consumption, one line per item, VAT shown. The platform issues one when a month closes; press the button if it is not here yet.',
  'billing.statements.created': 'Statement {{number}} is ready.',
  'billing.statements.exists': 'Statement {{number}} was already issued.',
  'billing.statements.noUsage': 'Nothing was charged in {{period}}, so there is no statement.',
  'billing.invoiceKind.usage_statement': 'Usage statement',
  'billing.usage.rates.vatOn': 'Prices include {{label}} at {{pct}}% (registration no. {{number}}): what you see is what you pay.',
  'billing.usage.rates.vatOff': 'These are the full amounts: no VAT is added to them.',
  'billing.explain.vat': 'VAT',
  'billing.explain.vatLine': '{{excl}} + VAT {{vat}} ({{pct}}%)',
  'billing.plan.title': 'Your plan',
  'billing.plan.none': 'No plan yet. You pay per message from your credits, the same as the Free plan. A paid plan adds credits every month and more chatbots, numbers, people and chatbot flows.',
  'billing.plan.current': 'You are on {{name}}.',
  'billing.plan.renews': 'Next charge {{date}}.',
  'billing.plan.seePlans': 'Compare plans',
  'billing.plan.changePlan': 'Change plan',
  'billing.plan.modalTitle': 'Plans',
  'billing.plan.modalBody': 'Plans are charged to your card on the 1st of every month. The credits a plan includes are added to your wallet with every charge and are spent first, on messages. The higher the plan, the less each message costs.',
  'billing.plan.free': 'Free',
  'billing.plan.perMonth': '{{amount}} / month',
  'billing.plan.includedCredit': '{{amount}} every month',
  'billing.plan.currentPlan': 'Current plan',
  'billing.plan.choose': 'Choose {{name}}',
  'billing.plan.confirm.title': 'Switch to {{name}}?',
  'billing.plan.confirm.subscribe': '{{now}} is charged to your card now for the rest of this month, with {{nowCredit}}. From {{next}}, {{amount}} is charged to the same card on the 1st of every month, with {{credit}} each time. If you have no card saved, you add one on the next page.',
  'billing.plan.confirm.subscribeFree': 'No monthly fee. You keep paying per message from your wallet.',
  'billing.plan.confirm.change': 'The unused part of your current plan is taken off the price of the new one for the days left in this month, and the difference is charged to your card. Included messaging credit moves the same way. Every plan is charged on the 1st.',
  'billing.plan.confirm.action': 'Switch plan',
  'billing.plan.switched': 'You are now on {{name}}.',
  'billing.plan.failed': 'Could not change the plan',
  'billing.plan.loadError': 'Could not load the plans.',
  'billing.credits.unit.one': 'credit',
  'billing.credits.unit.other': 'credits',
  'billing.creditValue': 'Credit value',
  'billing.creditValue.value': '1 credit = R1',
  'billing.creditValue.hint': 'Buy credits in rand. Every message and reply is priced in credits.',
  'billing.topup.payIn': 'Pay in',
  'billing.topup.buysCredits': '{{amount}} buys {{credits}}.',
  'billing.topup.youGet': 'You get',
  'billing.topup.rateNote': 'At the current rate of {{rate}} {{currency}} per credit.',
  'billing.alerts.low.emailPlaceholder': 'finance@example.com, owner@example.com',
  'billing.alerts.low.msisdnPlaceholder': '+27 82 000 1234',
  'billing.category.meta': 'Messenger and Instagram',
  'billing.referredBy.title': 'Referred by a partner',
  'billing.referredBy.body': '{{name}} introduced you to RepletoryReach. It costs you nothing and changes none of your prices.',
  'billing.referredBy.askTitle': 'Were you referred by somebody?',
  'billing.referredBy.askBody': 'If a partner introduced you to RepletoryReach, put their code in and they get credit for it. Your own prices do not change.',
  'billing.referredBy.codeLabel': 'Referral code',
  'billing.referredBy.codeHint': 'Eight letters and numbers, from the person who introduced you.',
  'billing.referredBy.submit': 'Add the code',
  'billing.referredBy.thanks': 'Thank you. Their code is on your account.',
  'billing.referredBy.failed': 'That code could not be added.',
  'billing.ledger.refundedTotal': 'Refunded',
  'billing.ledger.planFeesTotal': 'Plan fees',
  'billing.ledger.spentHint': 'Messages, AI and storage',
  'billing.ledger.planFeesHint': 'Your subscription, less any credit for a plan change',
  'billing.ledger.addedHint': 'Top-ups, grants and credits included with your plan',
  'billing.ledger.totalsNote': 'Added, refunded and paid by card, less spent and plan fees, is your balance when no filter is applied.',
  'billing.ledger.exportFailed': 'The ledger could not be exported. Try again.',
  'billing.ledger.exporting': 'Exporting…',
  'billing.plan.limits.title': 'What it allows',
  'billing.plan.limit.bots': 'Chatbots',
  'billing.plan.limit.numbers': 'WhatsApp numbers',
  'billing.plan.limit.team_members': 'Team members',
  'billing.plan.limit.flows': 'Chatbot flows',
  'billing.plan.limit.knowledge_documents': 'Knowledge base documents',
  'billing.plan.limit.unlimited': 'Unlimited',
  'billing.wallet.bought': 'Bought: {{amount}}',
  'billing.wallet.fromPlan': 'From your plan: {{amount}}',
  'billing.wallet.split.info': 'Credits from your plan are used first and are for messages. Your plan is paid by card, never from your credit.',
  'billing.wallet.split.infoLabel': 'About bought credits and credits from your plan',
  'billing.plan.prices.title': 'Message prices, in credits',
  'billing.plan.prices.marketing': 'Marketing message',
  'billing.plan.prices.utility': 'Utility message',
  'billing.plan.prices.service': 'Reply within 24 hours',
  'billing.plan.prices.cheaper': 'Lower message prices than you pay now',
  'billing.plan.refused.title': 'Not enough bought credits for {{name}}',
  'billing.plan.refused.body': 'A plan\'s fee is paid from credits you have bought. Bought credits left: {{bought}}. Needed: {{required}}. The {{included}} from your plan can only be spent on messages.',
  'billing.plan.refused.next': 'Top up {{shortfall}} or more, then choose {{name}} again.',
  'billing.usage.rates.view': 'Which prices to show',
  'billing.usage.rates.view.mine': 'Your prices',
  'billing.usage.rates.view.compare': 'Compare plans',
  'billing.usage.rates.changePlan': 'Change plan →',
  'billing.usage.rates.compareHint': 'What one unit costs on each plan, in credits. Your plan is marked.',
  'billing.usage.rates.yourPlan': 'Your plan',
  'billing.usage.rates.yourPlanPrice': 'Your {{plan}} price',
  'billing.usage.rates.payg': 'Pay-as-you-go price:',
  'billing.usage.rates.planNote': 'You are on {{plan}}. Where your price is lower than the pay-as-you-go price, that price is shown struck through beside yours.',
  'billing.explain.planPrice': 'Charged at your {{plan}} price (pay-as-you-go is {{list}})',
  'billing.explain.planPriceUnnamed': 'Charged at your plan\'s price (pay-as-you-go is {{list}})',
  'billing.topup.providerDisabled': 'That way of paying has just been switched off. Choose another one, or try again later.',
  'billing.alerts.methods.unavailable.title': 'Saving a card is not available right now',
  'billing.alerts.methods.unavailable.body': 'Cards you have already saved stay here, but automatic top-ups are paused until card payments are switched on again. You can still top up by hand.',
  'billing.topup.result.checking.title': 'Confirming your payment',
  'billing.topup.result.checking.body': 'Waiting for {{provider}} to confirm it. This usually takes a few seconds.',
  'billing.topup.result.checking.bodyPlain': 'This usually takes a few seconds.',
  'billing.topup.result.slow.title': 'Still confirming',
  'billing.topup.result.slow.body': '{{provider}} has not confirmed your payment yet. Your credits appear as soon as it does, so you can close this.',
  'billing.topup.result.success.title': 'Payment successful',
  'billing.topup.result.success.body': '{{credits}} added to your wallet.',
  'billing.topup.result.success.receipt': 'A receipt is on its way to your email.',
  'billing.topup.result.failed.title': 'Payment not completed',
  'billing.topup.result.failed.body': '{{provider}} did not complete this payment, so no credits were added. If money left your account, contact us with the reference below.',
  'billing.topup.result.paid': 'Paid',
  'billing.topup.result.added': 'Credits added',
  'billing.topup.result.balance': 'Balance now',
  'billing.topup.result.reference': 'Reference {{reference}}',
  'billing.topup.result.tryAgain': 'Try again',
  'billing.topup.result.done': 'Done',
  'billing.unit.10k_tokens.one': 'ten thousand tokens',
  'billing.unit.10k_tokens.other': 'ten thousand tokens',
  'billing.category.voice': 'Phone calls',
  'billing.source.voice': 'Phone calls',
  'billing.unit.minute.one': 'minute',
  'billing.unit.minute.other': 'minutes',
  'billing.unit.number.one': 'number',
  'billing.unit.number.other': 'numbers',
  'billing.unit.month.one': 'month',
  'billing.unit.month.other': 'months',
  'billing.wallet.held': 'Held for calls in progress: {{amount}}',
  'billing.wallet.heldHint': 'Credit set aside for a call that is under way, or a fee awaiting approval. Whatever is not used comes back when it ends.',
  'billing.plan.ends': 'Cancelled: it stays until {{date}} and is not charged again.',
  'billing.plan.keep': 'Keep my plan',
  'billing.plan.kept': 'You are keeping {{name}}. It renews as before.',
  'billing.plan.cancelScheduled': 'Your plan stays until {{date}}, then you move to the free plan. You will not be charged again.',
  'billing.plan.confirm.cancelAtEnd': 'Your {{name}} plan stays as it is until {{date}}, the end of the month you have paid for, and is not charged again. Then you move to {{free}} and pay-as-you-go prices. Nothing is deleted, and you can keep the plan any time before then.',
  'billing.plan.confirm.subscribeFull': '{{amount}} is charged to your card now for this month, with {{credit}}. After that it is charged to the same card on the 1st of every month. If you have no card saved, you add one on the next page.',
  'billing.plan.limit.own_whatsapp_account': 'Your own WhatsApp account',
  'billing.plan.limit.included': 'Included',
  'billing.plan.limit.notIncluded': 'Not included',
  'billing.usage.ownAccountNote': 'Messages on your own WhatsApp account are billed by Meta, not in credits.',
  'billing.plan.limit.booking_staff': 'Staff who take bookings',
  'billing.plan.limit.booking_services': 'Services to book',
  'billing.plan.limit.calendar_sync': 'Calendar sync',
  'billing.plan.limit.ticket_types': 'Ticket types',
  'billing.plan.limit.ticket_automations': 'Ticket automations',
  'billing.card.title': 'Card for your plan',
  'billing.card.description': 'Your plan is charged to this card on the 1st of every month. Credits you buy are for messages and other use, and never pay for the plan.',
  'billing.card.add': 'Add a card',
  'billing.card.replace': 'Replace card',
  'billing.card.current': '{{card}}, added {{date}}',
  'billing.card.missing': 'Your plan has no card to charge. Add one before the 1st so your plan carries on.',
  'billing.card.none': 'No card saved yet. You add one when you choose a paid plan.',
  'billing.card.checkHint': 'Adding or replacing a card opens a secure payment page for a small payment that saves the card. That payment goes into your credit, so nothing is lost.',
  'billing.card.declinedTitle': 'Your card was declined',
  'billing.card.useAnother': 'Use another card',
  'billing.card.result.planTitle': 'Payment successful',
  'billing.card.result.planBody': 'Your plan is on, and your card is saved for the 1st of every month.',
  'billing.card.result.cardTitle': 'Card saved',
  'billing.card.result.cardBody': 'Your plan is charged to this card from now on. The {{credits}} you paid went into your credit.',
  'billing.plan.changesTo': 'Changes to {{name}} on {{date}}.',
  'billing.plan.changeScheduled': 'You move to {{name}} on {{date}}. Until then you keep your current plan.',
  'billing.plan.confirm.downgrade': 'You keep your current plan until {{date}}, because this month is already paid for. Then you move to {{name}}, and its price is charged to your card on the 1st of every month.',
  'billing.card.ending': '{{brand}} ending {{last4}}',
  'billing.card.savedWith': 'Your card saved with {{provider}}',
  'billing.card.genericBrand': 'Card',
  'billing.ledger.paidByCardTotal': 'Paid by card',
  'billing.ledger.paidByCardHint': 'Plan fees paid by card, so your credit was not used',
  'billing.source.ai_api': 'AI tools',
  'billing.unit.1k_chars.one': 'thousand characters',
  'billing.unit.1k_chars.other': 'thousand characters',
  'billing.unit.image.one': 'picture',
  'billing.unit.image.other': 'pictures',
  'billing.plan.byInvoice.badge': 'Billed by invoice',
  'billing.plan.byInvoice.body': 'You pay for your plan by invoice: its fee is taken from your credit on the 1st, once your payment has reached us. To change or cancel your plan, contact RepletoryReach.',
  'billing.pastDue.invoiceBody': 'Your credit did not cover this month\'s plan fee. Nothing has been switched off. Pay your invoice, and once your payment reaches us and is added to your credit, the plan renews within the hour.',
  'billing.extra.title': 'Extra use',
  'billing.extra.description': 'Keep going when your credit runs out. Everything carries on at your plan\'s prices, up to a monthly limit you set, and is charged to your card.',
  'billing.extra.badge.paused': 'Paused',
  'billing.extra.notOnPlan': 'Available on {{plans}}.',
  'billing.extra.notOnPlanDefault': 'Available on Growth and Scale.',
  'billing.extra.noCard': 'Extra use is charged to a saved card. Add a card first, then switch it on here.',
  'billing.extra.addCard': 'Add a card',
  'billing.extra.switch': 'Keep going when my credit runs out',
  'billing.extra.switchHint': 'Charged to {{card}}.',
  'billing.extra.limit.label': 'Monthly limit ({{currency}})',
  'billing.extra.limit.hint': 'The most extra use can reach in a month, from {{min}} up to {{max}}.',
  'billing.extra.mode.label': 'How to pay',
  'billing.extra.mode.steps': 'In steps',
  'billing.extra.mode.steps.hint': 'Your card is charged each time extra use reaches the amount you choose.',
  'billing.extra.mode.upfront': 'Upfront',
  'billing.extra.mode.upfront.hint': 'Your card buys credit when your balance runs low, so your balance never goes below zero.',
  'billing.extra.mode.month_end': 'On the 1st, with your plan',
  'billing.extra.mode.month_end.hint': 'What you used is charged to your card on the 1st of the next month, with your plan.',
  'billing.extra.step.label': 'Step amount ({{currency}})',
  'billing.extra.step.hint': 'Your card is charged this much each time. At least {{min}}, and no more than your monthly limit.',
  'billing.extra.topup.label': 'Top-up amount ({{currency}})',
  'billing.extra.topup.hint': 'How much credit your card buys each time. At least {{min}}, and no more than your monthly limit.',
  'billing.extra.below.label': 'Top up when the balance drops under (credits)',
  'billing.extra.below.hint': 'Your card buys credit when your balance drops under this amount.',
  'billing.extra.priceNote': 'Charged at your plan\'s prices.',
  'billing.extra.consent': 'When you save with extra use on, you allow us to charge your saved card in the way you chose. You can switch it off at any time; what you used before then is still charged.',
  'billing.extra.saved.on': 'Extra use is on.',
  'billing.extra.saved.off': 'Extra use is off. Anything still owed is charged on the 1st.',
  'billing.extra.error.limitRequired': 'Enter a monthly limit.',
  'billing.extra.error.limitRange': 'Choose a limit from {{min}} to {{max}}.',
  'billing.extra.error.stepRequired': 'Enter a step amount.',
  'billing.extra.error.topupRequired': 'Enter a top-up amount.',
  'billing.extra.error.amountMin': 'At least {{min}}.',
  'billing.extra.error.amountOverLimit': 'No more than your monthly limit.',
  'billing.extra.error.belowNegative': 'Use 0 or more.',
  'billing.extra.error.EXTRA_USE_NOT_ON_PLAN': 'Your plan does not include extra use. Move up a plan to switch it on.',
  'billing.extra.error.EXTRA_USE_NO_CARD': 'Add a card first: extra use is charged to a saved card.',
  'billing.extra.error.EXTRA_USE_NOT_OFFERED': 'Extra use is not available at the moment.',
  'billing.extra.error.EXTRA_USE_BLOCKED': 'Extra use has been switched off for your account. Contact support to talk about it.',
  'billing.extra.error.EXTRA_USE_PAST_DUE': 'Your plan is waiting for payment. Pay your plan first, then switch extra use on.',
  'billing.extra.error.forbidden': 'Only the owner or an admin can change extra use.',
  'billing.extra.paused.title': 'Extra use is paused',
  'billing.extra.paused.declined': 'Your card was declined, so extra use is paused and sending stops at zero again. What you owe stays due.',
  'billing.extra.paused.declineReason': 'Reason given: {{reason}}',
  'billing.extra.paused.no_card': 'No card is saved, so extra use is paused. Add a card to switch it back on.',
  'billing.extra.paused.past_due': 'Your plan is waiting for payment, so extra use is paused. Pay your plan to carry on.',
  'billing.extra.paused.blocked': 'Extra use has been switched off for your account by our team. Contact support to talk about it.',
  'billing.extra.paused.not_on_plan': 'Your plan does not include extra use, so it is paused.',
  'billing.extra.paused.not_offered': 'Extra use is not available at the moment, so it is paused.',
  'billing.extra.paused.other': 'Extra use is paused.',
  'billing.extra.paused.payPlan': 'Pay your plan',
  'billing.extra.month.used': 'This month: {{used}} of {{limit}}',
  'billing.extra.month.room': '{{room}} left',
  'billing.extra.month.meter': 'Extra use this month',
  'billing.extra.month.owed': 'Owed: {{amount}}',
  'billing.extra.month.charged': 'Charged: {{amount}}',
  'billing.extra.next.step': 'Next charge: {{amount}}, when what you owe reaches it.',
  'billing.extra.next.topup': 'Next charge: {{amount}} for credit when your balance drops under {{below}}.',
  'billing.extra.next.monthEnd': 'Next charge: on {{date}}, with your plan.',
  'billing.extra.read.on': 'Extra use is on, up to {{limit}} a month.',
  'billing.extra.read.off': 'Extra use is off.',
  'billing.extra.read.steps': 'Paid in steps of {{amount}}.',
  'billing.extra.read.upfront': 'Paid upfront: {{amount}} for credit when the balance drops under {{below}}.',
  'billing.extra.read.monthEnd': 'Paid on the 1st, with the plan.',
  'billing.extra.read.who': 'Only the owner or an admin can change extra use.',
  'billing.extra.thisMonth': 'Extra use this month: {{used}} of {{limit}}',
  'billing.extra.alert.title': 'Your credit has run out, and extra use is keeping things going',
  'billing.extra.alert.body': 'Messages, replies and everything else carry on at your plan\'s prices. Extra use this month: {{used}} of {{limit}}. Top up to go back to using your credit.',
  'billing.extra.lowNote': 'When it runs out, extra use keeps things going.',
  'billing.extra.chipLabel': 'Extra use',
  'billing.extra.chipAmount': '{{used}} of {{limit}}',
  'billing.plan.limit.extra_use': 'Extra use',
  'billing.plan.limit.extra_use.hint': 'With a saved card, keep going after your credit runs out, at the plan\'s prices, up to a monthly limit you set.',
  'billing.plan.limit.flows.hint': 'Entry points (Welcome new customers, Returning customers, Anything else, After resolution) do not count.',
  'billing.alerts.kind.extra_use_started': 'Extra use started',
  'billing.alerts.kind.extra_use_charged': 'Extra use charged',
  'billing.alerts.kind.extra_use_declined': 'Extra use card declined',
  'billing.alerts.kind.extra_use_limit': 'Extra use limit reached',
  'billing.plan.extraUsePending': 'A payment for extra use is still being confirmed. Try again in a few minutes.'
};

export default billing;
